import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Space, Row, Col, Tag } from 'antd';
import { XIcon } from 'lucide-react';
import { Button, Card, Divider, Typography, Flex } from 'pink-lava-ui';
import {
  FormProvider,
  useForm,
  useFormContext,
  type DefaultValues,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from 'react-hook-form';
import { isEqual } from 'es-toolkit';
import { FilterBuilder, type ColumnProps, type FilterFieldType } from '@/components/ui/form/FilterBuilder';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useSelectOptionsStore } from '@/stores/selectOptionsStore';
import DownloadUploadActions, { type DownloadUploadActionsProps } from '@/components/ui/actions/DownloadUploadActions';
import dayjs from 'dayjs';

interface ListHeaderProps<T extends FieldValues> {
  title: string;
  filterFields: FilterFieldType<T>[];
  defaultValues?: Partial<T>;
  onFilter?: (_data: T) => void;
  onReset?: () => void;
  actions?: {
    primary?:
      | {
          label: string;
          onClick: () => void;
          icon?: ReactNode;
        }
      | ReactNode;
    secondary?:
      | {
          label: string;
          onClick: () => void;
          icon?: ReactNode;
        }
      | ReactNode;
    downloadUpload?: DownloadUploadActionsProps;
  };
  methods?: UseFormReturn<T>;
  colProps?: ColumnProps;
  hideFilterLabels?: Path<T>[];
  children?: ReactNode;
}

const ListHeader = <T extends FieldValues>({
  title,
  filterFields,
  defaultValues,
  onFilter,
  onReset,
  actions,
  methods: externalMethods,
  colProps = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 8,
  },
  hideFilterLabels,
  children,
}: ListHeaderProps<T>) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Use Zustand store for getting labels
  const { labels: labelsState } = useSelectOptionsStore();

  // URL params synchronization
  const { updateUrlParams, resetSpecificFilters, filters, removeParam } = useUrlParamsContext();

  // Combine defaultValues from props and URL params, excluding page & limit
  const combinedDefaultValues = useMemo(
    () =>
      ({
        ...defaultValues,
        ...filters,
      }) as Partial<T>,
    [defaultValues, filters]
  );

  const internalMethods = useForm<T>({
    defaultValues: {} as DefaultValues<T>,
  });

  const methods = externalMethods || internalMethods;
  const prevDefaultValuesRef = useRef<Partial<T>>(combinedDefaultValues);

  useEffect(() => {
    // Initialize the form default values
    if (!isInitialized && combinedDefaultValues) {
      methods.reset(combinedDefaultValues as DefaultValues<T>);
      setIsInitialized(true);
    }
    // Only reset if combinedDefaultValues actually changed
    if (JSON.stringify(prevDefaultValuesRef.current) !== JSON.stringify(combinedDefaultValues)) {
      // Force update all form fields, including clearing empty arrays
      Object.keys(combinedDefaultValues).forEach(k => {
        const key = k as Path<T>;
        const value = combinedDefaultValues[key as keyof T];
        if (Array.isArray(value) && value.length === 0) {
          methods.setValue(key, undefined as any);
        } else {
          methods.setValue(key, value as any);
        }
      });

      // Also clear any fields that are no longer in combinedDefaultValues
      const currentValues = methods.getValues();
      Object.keys(currentValues).forEach(k => {
        const key = k as Path<T>;
        if (!(key in combinedDefaultValues)) {
          methods.setValue(key, undefined as any);
        }
      });

      prevDefaultValuesRef.current = combinedDefaultValues;
    }
  }, [combinedDefaultValues, methods, isInitialized]);

  const handleSubmit = (data: T) => {
    // Process data to format date-range fields as YYYY-MM-DD
    const processedData = { ...data };

    // Check each field to see if it's a date-range field
    filterFields.forEach(field => {
      if (field.type === 'date-range' && Array.isArray(processedData[field.name])) {
        // Format each date in the range as YYYY-MM-DD, convert null/undefined to empty string
        const formattedDates = processedData[field.name].map((dateValue: any) => {
          if (dateValue) {
            const date = dayjs(dateValue);
            return date.isValid() ? date.format('YYYY-MM-DD') : dateValue;
          }
          return ''; // Convert null/undefined to empty string to preserve array structure
        });

        processedData[field.name] = formattedDates;
      }
    });

    updateUrlParams({ ...processedData, page: 1 });
    onFilter?.(processedData);
  };

  const handleReset = () => {
    // Get current values to preserve hidden filters
    const currentValues = methods.getValues();

    // Keep only hidden filter values
    const preservedValues: Partial<T> = {};
    hideFilterLabels?.forEach(key => {
      const value = currentValues[key];
      if (value !== undefined && value !== null && value !== '') {
        preservedValues[key] = value;
      }
    });

    // Reset form with preserved values
    methods.reset(preservedValues as DefaultValues<T>);

    // Reset URL params, preserving hidden filters
    resetSpecificFilters((hideFilterLabels as string[]) || []);

    onReset?.();
  };

  // Helper function to format date values
  const formatFilterLabel = (value: any, key: string): string => {
    // Check if this is a date field
    const dateField = filterFields.find(field => field.name === key);
    const isDateField = dateField?.type === 'date-range';

    if (isDateField && value) {
      try {
        // Handle date range arrays
        if (Array.isArray(value)) {
          const [startDate, endDate] = value;

          // Format start date
          const formattedStart =
            startDate && startDate !== ''
              ? dayjs(startDate).isValid()
                ? dayjs(startDate).format('DD MMM YYYY')
                : startDate
              : null;

          // Format end date
          const formattedEnd =
            endDate && endDate !== ''
              ? dayjs(endDate).isValid()
                ? dayjs(endDate).format('DD MMM YYYY')
                : endDate
              : null;

          // Display with labels based on which dates are present
          if (formattedStart && formattedEnd) {
            return `${formattedStart} - ${formattedEnd}`;
          } else if (formattedStart && !formattedEnd) {
            return `From: ${formattedStart}`;
          } else if (!formattedStart && formattedEnd) {
            return `To: ${formattedEnd}`;
          }

          return '';
        } else {
          // Handle single date values
          const date = dayjs(value);
          if (date.isValid()) {
            // Format in local timezone to avoid GMT conversion
            return date.format('DD MMM YYYY');
          }
        }
      } catch (error) {
        // If parsing fails, return the original value
        console.warn('Failed to parse date value:', error);
      }
    }

    // For non-date fields or if date parsing fails, use the original label or value
    return filterLabels?.[key]?.[value] || value;
  };

  // Simplified filter labels computation
  const filterLabels = useMemo(() => {
    const labels: Record<string, Record<string | number, string>> = {};

    filterFields.forEach(field => {
      const fieldLabels: Record<string | number, string> = {};

      if (field.apiUrl) {
        // Get labels from Zustand store for API-based fields
        if (labelsState?.[field.apiUrl]) {
          for (const key in labelsState[field.apiUrl]) {
            fieldLabels[key] = labelsState[field.apiUrl][key];
          }
        }
      } else if (field.options) {
        // Handle static options
        field.options.forEach(option => {
          fieldLabels[option.value] = option.label;
        });
      }

      if (Object.keys(fieldLabels).length > 0) {
        labels[field.name!] = fieldLabels;
      }
    });

    return labels;
  }, [filterFields, labelsState]);

  const filterFieldsWithoutHideLabels = useMemo(() => {
    return Object.entries(filters).filter(([key]) => !hideFilterLabels?.includes(key as Path<T>));
  }, [filters, hideFilterLabels]);

  return (
    <Card className="w-full m-4! mb-0!" size="small" rounded="large">
      <div className="flex flex-row justify-between items-center">
        <Typography.Heading level={4}>{title}</Typography.Heading>
        <Space>
          {actions?.primary &&
            (typeof actions.primary === 'object' && 'label' in actions.primary ? (
              <Button
                shape="round"
                size="middle"
                type="primary"
                icon={<div>{actions.primary.icon}</div>}
                onClick={actions.primary.onClick}
              >
                {actions.primary.label}
              </Button>
            ) : (
              <>{actions.primary}</>
            ))}
          {actions?.secondary &&
            (typeof actions.secondary === 'object' && 'label' in actions.secondary ? (
              <Button
                shape="round"
                size="middle"
                type="secondary"
                icon={<div>{actions.secondary.icon}</div>}
                onClick={actions.secondary.onClick}
              >
                {actions.secondary.label}
              </Button>
            ) : (
              <>{actions.secondary}</>
            ))}
          <DownloadUploadActions {...actions?.downloadUpload} />
        </Space>
      </div>
      {children}
      <Divider className="!my-4" />
      <form className="w-full" onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormProvider {...methods}>
          <Row gutter={[16, 16]}>
            <Col flex="auto">
              <FilterBuilder
                fields={filterFields}
                methods={methods}
                colProps={colProps}
                handleSubmit={handleSubmit}
                handleReset={handleReset}
                hideFilterLabels={hideFilterLabels}
              />
            </Col>
            <Col span={24} className="hidden! md:flex! items-end! min-[1660px]:!flex-none">
              <SetFilterButton handleSubmit={handleSubmit} />
            </Col>
          </Row>
        </FormProvider>
      </form>
      {filterFieldsWithoutHideLabels.length > 0 && <Divider className="!my-4" />}
      <Flex gap={8} wrap>
        {filterFieldsWithoutHideLabels.length > 0 && (
          <Button className="px-2!" size="middle" shape="round" type="ghost" onClick={handleReset}>
            Clear Filter
          </Button>
        )}
        {filterFieldsWithoutHideLabels.map(([key, value]) => {
          // Check if this is a date-range field
          const dateField = filterFields.find(field => field.name === key);
          const isDateRangeField = dateField?.type === 'date-range';

          // For date-range fields, show as a single combined tag instead of separate tags
          if (isDateRangeField && Array.isArray(value)) {
            return (
              <Tag
                key={`${key}`}
                className="rounded-full! text-base! py-1! px-3! flex! items-center! gap-1! border-cyan-500! bg-white! m-0! min-w-0! max-w-full! overflow-hidden!"
                closable
                closeIcon={<XIcon size={16} />}
                onClose={() => removeParam(key)}
              >
                <span className="flex-1 truncate block">{formatFilterLabel(value, key)}</span>
              </Tag>
            );
          }

          // For non-date-range array fields, show individual tags
          if (Array.isArray(value) && !isDateRangeField) {
            return value.map((item, index) => (
              <Tag
                key={`${key}-${item}`}
                className="rounded-full! text-base! py-1! px-3! flex! items-center! gap-1! border-cyan-500! bg-white! m-0! min-w-0! max-w-full! overflow-hidden!"
                closable
                closeIcon={<XIcon size={16} />}
                onClose={() => removeParam(key, index)}
              >
                <span className="flex-1 truncate block">{formatFilterLabel(item, key)}</span>
              </Tag>
            ));
          }

          // For non-array fields
          if (!Array.isArray(value)) {
            return (
              <Tag
                key={`${key}-${value}`}
                className="rounded-full! text-base! py-1! px-3! flex! items-center! gap-1! border-cyan-500! bg-white! m-0! min-w-0! max-w-full! overflow-hidden!"
                closable
                closeIcon={<XIcon size={16} />}
                onClose={() => removeParam(key)}
              >
                <span className="flex-1 truncate block">{formatFilterLabel(value, key)}</span>
              </Tag>
            );
          }

          return null;
        })}
      </Flex>
    </Card>
  );
};

export const SetFilterButton = ({ handleSubmit }: { handleSubmit: (_data: any) => void }) => {
  const { filters } = useUrlParamsContext();
  const methods = useFormContext();

  const values = methods.watch();

  const disableFilterButton = useMemo(() => {
    return isEqual(
      Object.fromEntries(Object.entries(values).filter(([_, v]) => v !== undefined && v !== null && v !== '')),
      filters
    );
  }, [filters, values]);

  return (
    <Button
      block
      size="middle"
      shape="round"
      type="secondary"
      disabled={disableFilterButton}
      onClick={methods.handleSubmit(handleSubmit)}
    >
      Terapkan
    </Button>
  );
};

export default ListHeader;
export type { ListHeaderProps };
